import { useState, type ReactNode } from "react";
import { useAuth } from "@clerk/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@ndd/ui";
import { ApiRequestError, authenticatedApi } from "@/lib/api";

interface ObservationDeleteDialogProps {
  rdId: string;
  noteId: string;
  onDeleted: () => void;
  trigger: ReactNode;
}

/**
 * Confirm-and-delete dialog for an observation note. Soft-deletes via the
 * REST route; recoverable, since observation notes are not compliance records.
 */
export function ObservationDeleteDialog({
  rdId,
  noteId,
  onDeleted,
  trigger,
}: ObservationDeleteDialogProps) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirm() {
    setError(null);
    setIsDeleting(true);

    try {
      await authenticatedApi(getToken, `/api/rds/${rdId}/observations/${noteId}`, {
        method: "DELETE",
      });
      setOpen(false);
      onDeleted();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        const body = err.body as { error?: unknown } | undefined;
        setError(
          typeof body?.error === "string" ? body.error : "Failed to delete observation."
        );
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this observation?</AlertDialogTitle>
          <AlertDialogDescription>
            It will be removed from this RD&apos;s observations. This can be restored
            later if needed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void handleConfirm();
            }}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
